import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAdminAgentScratchpadIndex } from '@apps/web/features/admin_agent_scratchpad/api/queries';
import {
    DEFAULT_PER_PAGE,
    DEFAULT_SCOPE_KIND,
    SEARCH_DEBOUNCE_MS,
} from '@apps/web/features/admin_agent_scratchpad/model/constants';
import {
    isScopeSelectionValid,
    normalizeScopeTypes,
    normalizeTags,
    type ScratchpadSearch,
    type ScratchpadSearchPatch,
    serializeScopeTypes,
} from '@apps/web/features/admin_agent_scratchpad/model/search_params';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';

const route = getRouteApi('/_app/admin/agent/scratchpad/');

type PatchFn = (patch: ScratchpadSearchPatch) => void;

/**
 * Wires URL-synced filters into the API. The URL is the only state owner;
 * the search input keeps a local draft so keystrokes don't push into
 * history until debounced.
 */
export function useScratchpadIndex(search: ScratchpadSearch) {
    const navigate = route.useNavigate();
    const routeSearchValue = search.search ?? '';
    const [searchDraft, setSearchDraft] = useState(routeSearchValue);
    const debouncedSearchDraft = useDebouncedValue(
        searchDraft,
        SEARCH_DEBOUNCE_MS,
    );
    const searchDraftEditedRef = useRef(false);

    useEffect(() => {
        searchDraftEditedRef.current = false;
        setSearchDraft(routeSearchValue);
    }, [routeSearchValue]);

    const patchSearch = useCallback<PatchFn>(
        (patch) => {
            void navigate({
                search: (previous) => {
                    const next = { ...previous, ...patch };
                    const scopeTypes =
                        next.scope_type === undefined
                            ? []
                            : Array.isArray(next.scope_type)
                              ? next.scope_type
                              : [next.scope_type];
                    next.scope_type = serializeScopeTypes(scopeTypes);
                    if ('tag' in patch && patch.tag?.length === 0) {
                        next.tag = undefined;
                    }
                    return next;
                },
                replace: true,
            });
        },
        [navigate],
    );

    useEffect(() => {
        if (!searchDraftEditedRef.current) {
            return;
        }
        const trimmed = debouncedSearchDraft.trim();
        const nextSearch = trimmed === '' ? undefined : trimmed;
        if ((routeSearchValue.trim() || undefined) === nextSearch) {
            return;
        }
        patchSearch({ search: nextSearch, page: 1 });
    }, [debouncedSearchDraft, patchSearch, routeSearchValue]);

    // Default the bare route to global-only so /admin/agent/scratchpad renders
    // without a required picker interaction (the API rejects an implicit
    // cross-org listing). We do not silently collapse or drop URL-supplied
    // scope kinds — those flow through untouched and the validity gate below
    // keeps us from firing a request the API is guaranteed to 422.
    const urlScopeTypes = normalizeScopeTypes(search);
    const requestScopeTypes =
        urlScopeTypes.length === 0 && search.cross_scope !== true
            ? [DEFAULT_SCOPE_KIND]
            : urlScopeTypes;
    const requestSearch: ScratchpadSearch =
        urlScopeTypes.length === 0 && search.cross_scope !== true
            ? { ...search, scope_type: [DEFAULT_SCOPE_KIND] }
            : search;
    const selectionIsValid = isScopeSelectionValid(requestSearch);

    const query = useAdminAgentScratchpadIndex(
        {
            page: search.page,
            perPage: DEFAULT_PER_PAGE,
            scopeType: requestScopeTypes,
            scopeId: search.scope_id,
            tag: [...normalizeTags(search)],
            volatility: search.volatility,
            freshness: search.freshness,
            search: search.search,
            includeRetired: search.include_retired,
            crossScope: search.cross_scope,
            sort: search.sort,
            direction: search.direction,
        },
        { keepPreviousData: true, enabled: selectionIsValid },
    );

    return {
        query,
        data: query.data,
        isLoading: query.isLoading && query.data === undefined,
        isValidating: query.isValidating,
        hasError: query.error !== undefined,
        // True when the current filter combination isn't valid to fire — the
        // page renders a placeholder in this state instead of an error card.
        selectionIsValid,
        selectedScopeTypes: urlScopeTypes,
        searchDraft,
        setSearchDraft: (value: string) => {
            searchDraftEditedRef.current = true;
            setSearchDraft(value);
        },
        clearSearchDraft: () => {
            searchDraftEditedRef.current = true;
            setSearchDraft('');
        },
        patchSearch,
    };
}
